import { useMemo } from 'react';
import { useMonitoringTrailing } from '../../../hooks/useMonitoringTrailing';
import type { OverviewCategory } from '../../../services/dmMonitoringOverview';
import type { HistoryBucket } from '../../../services/dmMonitoringOverviewHistory';
import { GranularityToggle, type GranularityOption } from '../../accounts/GranularityToggle';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import { CATEGORY_LEGEND } from './overviewLegend';
import type { ReportingPeriod } from './overviewPeriod';
import { OverTimeBody, WindowSubtitle } from './OverTimeCardParts';
import { BUCKET_OPTIONS, bucketWindow, CATEGORY_FIELD } from './overTimeRows';
import { buildPerPersonChart, type PerPersonMeasure } from './perPersonRows';
import { useStoredChoice } from './useStoredChoice';

const STORAGE_KEY_PREFIX = 'monitoring_overview_per_person_';
const MEASURE_KEY_PREFIX = 'monitoring_overview_per_person_measure_';

function measureOptions(category: OverviewCategory): GranularityOption<PerPersonMeasure>[] {
    return [
        { key: 'events', label: CATEGORY_LEGEND[category].label },
        { key: 'patients', label: 'Patients' }
    ];
}

const MEASURE_OPTIONS: Readonly<Record<OverviewCategory, GranularityOption<PerPersonMeasure>[]>> = {
    notification: measureOptions('notification'),
    message: measureOptions('message')
};

function perPersonCopy(noun: string, measure: PerPersonMeasure, period: ReportingPeriod) {
    const lowerNoun = noun.toLowerCase();
    if (measure === 'events') {
        return {
            subtitle: period.label,
            info: `How many ${lowerNoun} each person handled.`,
            empty: `Nothing handled ${period.phrase}`
        };
    }
    return {
        subtitle: `Patients handled per person · ${period.label.toLowerCase()}`,
        info: `How many patients each person handled ${lowerNoun} for.`,
        empty: `No patients handled ${period.phrase}`
    };
}

interface PerPersonTogglesProps {
    category: OverviewCategory;
    measure: PerPersonMeasure;
    onMeasureChange: (next: PerPersonMeasure) => void;
    bucket: HistoryBucket;
    onBucketChange: (next: HistoryBucket) => void;
}

function PerPersonToggles(props: PerPersonTogglesProps) {
    const { category, measure, onMeasureChange, bucket, onBucketChange } = props;
    const noun = CATEGORY_LEGEND[category].label;
    return (
        <div className="flex flex-wrap items-center justify-end gap-2">
            <GranularityToggle
                value={measure}
                options={MEASURE_OPTIONS[category]}
                onChange={onMeasureChange}
                ariaLabel={`${noun} per person: count ${noun.toLowerCase()} or patients`}
            />
            <GranularityToggle
                value={bucket}
                options={BUCKET_OPTIONS}
                onChange={onBucketChange}
                ariaLabel={`${noun} per person: day, week or month`}
            />
        </div>
    );
}

export function HandledPerPersonCard({ category }: { category: OverviewCategory }) {
    const noun = CATEGORY_LEGEND[category].label;
    const [bucket, setBucket] = useStoredChoice(`${STORAGE_KEY_PREFIX}${category}`, BUCKET_OPTIONS);
    const [measure, setMeasure] = useStoredChoice(
        `${MEASURE_KEY_PREFIX}${category}`,
        MEASURE_OPTIONS[category]
    );
    const perPerson = useMonitoringTrailing('per-person', bucket);
    // Labels follow the bucket the data is for, so the previous bucket never wears the new name.
    const shownBucket = perPerson.data?.bucket ?? bucket;
    const series = perPerson.data?.[CATEGORY_FIELD[category]];
    const chart = useMemo(
        () => (series ? buildPerPersonChart(series, shownBucket, measure) : null),
        [series, shownBucket, measure]
    );
    const copy = perPersonCopy(noun, measure, bucketWindow(shownBucket));
    return (
        <OverviewCard
            title={`${noun} per person`}
            titleClassName="text-[15px]"
            subtitle={<WindowSubtitle label={copy.subtitle} archive={perPerson.data?.archive} />}
            info={copy.info}
            wrapAction
            action={
                <PerPersonToggles
                    category={category}
                    measure={measure}
                    onMeasureChange={setMeasure}
                    bucket={bucket}
                    onBucketChange={setBucket}
                />
            }
        >
            <OverTimeBody
                chart={chart}
                isLoading={perPerson.isLoading}
                isSwitching={perPerson.isSwitching}
                errorMessage={
                    perPerson.isError ? `Couldn't load ${noun.toLowerCase()} per person` : null
                }
                emptyMessage={copy.empty}
                onRetry={() => void perPerson.retry()}
            />
        </OverviewCard>
    );
}
